import React, { useRef, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, List, ListItem, ListItemText, MenuItem,
    Select, SelectChangeEvent, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { AuthService } from '../../services/auth.service';
import { BuyerService } from '../../services/buyer.service';
import { BuyerDocumentKind, IBuyerAttachment } from '../../interfaces/garments/buyer.interface';
import { useToast } from '../../context/ToastContext';
import { triggerDownload } from '../statutoryRegisters/shared';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

interface BuyerAttachmentsProps {
    buyerId: string;
    attachments: IBuyerAttachment[];
    canManage: boolean;
    onChanged: () => void;
}

// Mirrors the shared backend FileStorageService.ALLOWED_CONTENT_TYPES + the 10MB
// spring.servlet.multipart.max-file-size cap (see BuyerAttachmentService's Javadoc) — stated up
// front so an unsupported file is refused here rather than surfacing a backend 400.
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const DOCUMENT_KIND_OPTIONS: { value: BuyerDocumentKind; label: string }[] = [
    { value: 'COC', label: 'Code of Conduct' },
    { value: 'AUDIT_REPORT', label: 'Audit Report' },
    { value: 'CERTIFICATE', label: 'Certificate' },
    { value: 'OTHER', label: 'Other' },
];

const DOCUMENT_KIND_LABEL: Record<BuyerDocumentKind, string> = {
    COC: 'CoC',
    AUDIT_REPORT: 'Audit Report',
    CERTIFICATE: 'Certificate',
    OTHER: 'Other',
};

const formatSize = (bytes?: number | null): string => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : formatDateUtil(iso);
};

/** Attachment upload/list/download/delete for a buyer (garments-merchandising 7.5): CoC / audit /
 *  certificate documents, tagged by `documentKind`. Mirrors `StyleAttachments.tsx`. */
export function BuyerAttachments({ buyerId, attachments, canManage, onChanged }: BuyerAttachmentsProps) {
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [documentKind, setDocumentKind] = useState<BuyerDocumentKind>('COC');
    const [uploading, setUploading] = useState(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<IBuyerAttachment | null>(null);

    const institutionId = () => AuthService.getSelectedInstitution();

    const handleKindChange = (e: SelectChangeEvent) => setDocumentKind(e.target.value as BuyerDocumentKind);

    const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.showError('Unsupported file type. Accepted: PDF, JPG, PNG, DOC, DOCX.');
            return;
        }
        if (file.size > MAX_SIZE_BYTES) {
            toast.showError('File is too large. The limit is 10MB.');
            return;
        }

        const instId = institutionId();
        if (!instId) return;
        setUploading(true);
        try {
            await BuyerService.uploadBuyerAttachment(instId, buyerId, file, documentKind);
            toast.showSuccess('Attachment uploaded');
            onChanged();
        } catch (err) {
            toast.showApiError(err, 'Failed to upload attachment');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (attachment: IBuyerAttachment) => {
        const instId = institutionId();
        if (!instId) return;
        setDownloadingId(attachment.id);
        try {
            const blob = await BuyerService.downloadBuyerAttachment(instId, buyerId, attachment.id);
            triggerDownload(blob, attachment.fileName);
        } catch (err) {
            toast.showApiError(err, 'Failed to download attachment');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!pendingDelete) return;
        const instId = institutionId();
        if (!instId) return;
        setDeletingId(pendingDelete.id);
        try {
            await BuyerService.deleteBuyerAttachment(instId, buyerId, pendingDelete.id);
            toast.showSuccess('Attachment deleted');
            onChanged();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete attachment');
        } finally {
            setDeletingId(null);
            setPendingDelete(null);
        }
    };

    return (
        <Box data-help-section="merchandising.buyerDocs.section.list">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Compliance Documents</Typography>
                {canManage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Select
                            data-help="merchandising.buyerDocs.kind"
                            size="small"
                            value={documentKind}
                            onChange={handleKindChange}
                            data-testid="buyer-attachment-kind-select"
                        >
                            {DOCUMENT_KIND_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                        <Button
                            data-help="merchandising.buyerDocs.upload"
                            size="small"
                            component="label"
                            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                            disabled={uploading}
                        >
                            Upload
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept={ACCEPTED_EXTENSIONS}
                                onChange={handleFileChosen}
                                data-testid="buyer-attachment-input"
                            />
                        </Button>
                    </Box>
                )}
            </Box>
            <Typography variant="caption" color="text.secondary">
                Accepted: PDF, JPG, PNG, DOC, DOCX. Maximum size: 10MB.
            </Typography>
            <List dense>
                {attachments.map(a => (
                    <ListItem
                        key={a.id}
                        secondaryAction={(
                            <Box>
                                <IconButton
                                    data-help="merchandising.buyerDocs.download"
                                    size="small"
                                    onClick={() => handleDownload(a)}
                                    disabled={downloadingId === a.id}
                                    aria-label={`Download ${a.fileName}`}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                                {canManage && (
                                    <IconButton
                                        data-help="merchandising.buyerDocs.delete"
                                        size="small"
                                        onClick={() => setPendingDelete(a)}
                                        disabled={deletingId === a.id}
                                        aria-label={`Delete ${a.fileName}`}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    >
                        <ListItemText
                            primary={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {a.fileName}
                                    <Chip label={DOCUMENT_KIND_LABEL[a.documentKind]} size="small" variant="outlined" />
                                </Box>
                            )}
                            secondary={`${formatSize(a.sizeBytes)} · Uploaded ${formatDate(a.createdAt)}`}
                        />
                    </ListItem>
                ))}
                {attachments.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No attachments yet.
                    </Typography>
                )}
            </List>
            <ConfirmDialog
                open={!!pendingDelete}
                title="Delete attachment"
                message={`Delete attachment "${pendingDelete?.fileName}"? This cannot be undone.`}
                confirmLabel="Delete"
                confirmColor="error"
                submitting={!!deletingId}
                onCancel={() => setPendingDelete(null)}
                onConfirm={handleDeleteConfirmed}
            />
        </Box>
    );
}

export default BuyerAttachments;
